// Shell — the portal navigation and outlet.
//
// Nav gating is USABILITY, not security (TD-030). A role-denied item is hidden; a licence-denied
// one stays visible and locked, so the commercial offer is discoverable rather than merely
// enforced. Either way the server decides.
//
// RESPONSIVE BEHAVIOUR. On a wide screen the nav is a fixed rail. On a narrow one it is a drawer
// behind a menu button — because the rail previously collapsed into a horizontal strip that wrapped
// onto three lines and pushed the actual page below the fold. An eight-item admin nav does not fit
// across a phone, and pretending it does costs the user the top third of the screen.
//
// The drawer closes on selection, on the backdrop, and on Escape, and the button reports its state
// through aria-expanded so it is operable without sight of the animation.
import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { canDetail, type PortalKey } from '../lib/capabilities'

interface NavItem { to: string; label: string; cap?: string }

const NAV: Record<PortalKey, { title: string; items: NavItem[] }> = {
  tc: {
    title: 'Town Council',
    items: [
      { to: 'dashboard', label: 'Dashboard' },
      { to: 'tenders', label: 'Tenders' },
      { to: 'suppliers', label: 'Suppliers' },
      { to: 'audit', label: 'Audit trail' },
    ],
  },
  // Ordered by WHOSE DAY IT IS, not by system structure: the work an officer does, then the
  // things they consult. Every item below the first two is capability-gated, so a call centre
  // agent, a Property Officer and a Property Manager each open this portal and see a different,
  // smaller app — not the full menu with most of it refusing them.
  ams: {
    title: 'Arrears Management',
    items: [
      { to: 'overview', label: 'Overview' },
      { to: 'inquiry', label: 'Resident inquiry', cap: 'arrears.inquiry' },
      { to: 'callcentre', label: 'Call centre', cap: 'arrears.contact.queue' },
      { to: 'queue', label: 'Work queue' },
      { to: 'visits', label: 'House visits', cap: 'arrears.visit' },
      { to: 'approvals', label: 'Approvals', cap: 'arrears.approvals.view' },
      { to: 'outreach', label: 'Outreach', cap: 'arrears.recommend' },
      { to: 'legal', label: 'Legal tracker', cap: 'arrears.legal.view' },
      { to: 'reports', label: 'Reports', cap: 'arrears.reports.view' },
      // Reference, below the day's work. Everything above is something an officer does today;
      // these are the framework they do it under and the words it uses.
      { to: 'rules', label: 'Hardship rules' },
      { to: 'glossary', label: 'Glossary' },
      { to: 'demo', label: 'Guided demo' },
    ],
  },
  bid: {
    title: 'Tenderer',
    items: [
      { to: 'dashboard', label: 'Dashboard' },
      { to: 'opportunities', label: 'Opportunities' },
      { to: 'library', label: 'Document library', cap: 'bid.prepare' },
    ],
  },
  ma: {
    title: 'Managing Agent',
    items: [
      { to: 'dashboard', label: 'Dashboard' },
      { to: 'clients', label: 'Clients' },
    ],
  },
  audit: {
    title: 'Auditor',
    items: [{ to: 'dashboard', label: 'Assigned reviews', cap: 'audit.review' }],
  },
  dev: {
    title: 'Developer',
    items: [
      { to: 'dashboard', label: 'Platform health' },
      { to: 'templates', label: 'Templates' },
      { to: 'prompts', label: 'Prompt registry' },
    ],
  },
}

export default function Shell({ portal }: { portal: PortalKey }) {
  const { identity, me } = useAuth()
  const nav = NAV[portal]
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  // Navigating closes the drawer. Without this, following a link on a phone leaves the drawer over
  // the page you just asked for.
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [open])

  const items = nav.items.map(item => {
    const verdict = item.cap ? canDetail(identity, item.cap) : ({ ok: true } as const)
    // A role denial hides the item entirely; a licence denial shows it locked. Two different
    // meanings, so two different treatments.
    if (!verdict.ok && verdict.why !== 'feature') return null
    const locked = !verdict.ok
    return (
      <li key={item.to}>
        {locked ? (
          <span className="nav-item nav-item-locked" title="Not included in your licence">
            {item.label} <span className="lock">Premium</span>
          </span>
        ) : (
          <NavLink to={item.to} className={({ isActive }) =>
            `nav-item${isActive ? ' nav-item-active' : ''}`}>
            {item.label}
          </NavLink>
        )}
      </li>
    )
  })

  return (
    <div className={`shell shell-${portal}${open ? ' shell-open' : ''}`}>
      {/* Narrow screens only. The rail carries the brand on a wide one. */}
      <header className="topbar">
        <button className="burger" aria-label="Menu" aria-expanded={open}
                aria-controls="portal-nav" onClick={() => setOpen(o => !o)}>
          <span /><span /><span />
        </button>
        <div className="topbar-title">
          <b>TownForge</b>
          <small>{nav.title}</small>
        </div>
      </header>

      {/* Sits above the page on a phone, beside it on a desktop. One element, not two, so the nav
          never renders twice and never drifts out of step with itself. */}
      {open && <div className="scrim" onClick={() => setOpen(false)} aria-hidden="true" />}

      <nav id="portal-nav" className="rail">
        <Link to="/" className="rail-brand">TownForge</Link>
        <div className="rail-portal">{nav.title}</div>
        <ul className="rail-items">{items}</ul>

        {me?.org && (
          <div className="rail-org">
            <div className="rail-org-name">{me.org.name}</div>
            <div className="rail-org-role">{me.designation ?? me.role}</div>
            {/* Development sign-in only. With real auth on, authEnabled is true and this is gone.
                A LINK, not a button that clears storage: clearing it does not sign anyone out,
                because the server falls back to its configured identity and signs you straight
                back in as that. */}
            {!me.authEnabled && <Link className="linkish" to="/signin">Switch user</Link>}
          </div>
        )}
      </nav>

      <main className="main">
        <Outlet />
      </main>
    </div>
  )
}
